import {
  dueDateWarning,
  signatureRequestExpiry,
} from "components/Submission/helpers/dueDateWarnings";
import {
  buildOrderedConfig,
  toReviewerInputs,
} from "components/Submission/helpers/reviewerConfiguration";
import { Reviewer } from "components/Submission/ReviewersEditList";
import { Issue, ReviewMode, SignatureType, SubmissionStatus } from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSubmissionInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useSubmissionInputValidation";
import { useDebouncedCallback } from "use-debounce";

import { useReviewerPreparationContext } from "./useReviewerPreparationContext";

type ContextInput = Pick<
  ReturnType<typeof useReviewerPreparationContext>,
  "tenantId" | "submission" | "documents" | "status"
>;

/** Debounced backend validation of the config a save would write, plus its display slices. */
export function useReviewerPreparationValidation({
  tenantId,
  submission,
  documents,
  status,
  submissionId,
  reviewersToPersist,
  signer,
  reviewRequired,
  peerReviewToPersist,
  reviewMode,
  signatureType,
}: ContextInput & {
  submissionId: string;
  reviewersToPersist: Reviewer[];
  signer: SubmissionReviewerSigner | null;
  reviewRequired: boolean;
  peerReviewToPersist: boolean;
  reviewMode: ReviewMode;
  signatureType: SignatureType;
}) {
  const validationDocuments = useMemo(
    () => documents.map((d) => ({ documentId: d.document.id, type: d.type })),
    [documents]
  );
  const validateSubmissionInput = useSubmissionInputValidation(validationDocuments);
  const [issues, setIssues] = useState<Issue[]>([]);

  const revalidate = useCallback(async (): Promise<Issue[]> => {
    // Validate the config a save would write, not the one on screen.
    const ordered = buildOrderedConfig(reviewersToPersist, signer);
    const result = await validateSubmissionInput({
      id: submissionId,
      // tenantId is overwritten by the validation hook (from the route params);
      // it's supplied here only to satisfy the required input field.
      tenantId,
      title: submission?.title ?? "",
      recipient: submission?.recipient ?? "",
      // The submission-level fields are not editable on this step but the
      // validator requires them, so pass the persisted values through.
      dueDate: submission?.dueDate ?? undefined,
      submissionType: submission?.submissionType,
      submissionMethod: submission?.submissionMethod ?? undefined,
      facilityIds: (submission?.facilitySubmissions ?? []).map((fs) => fs.facility.id),
      reviewRequired,
      peerReviewRequired: peerReviewToPersist,
      reviewMode,
      signatureType,
      reviewers: ordered.map((c) => ({
        userId: c.id,
        reviewOrder: c.reviewOrder,
        isStaff: c.isStaff,
        isRequired: true,
        isSigner: c.isSigner,
        dueDate: c.dueDate,
      })),
    });
    setIssues(result);
    return result;
  }, [
    reviewersToPersist,
    signer,
    validateSubmissionInput,
    submissionId,
    tenantId,
    submission?.title,
    submission?.recipient,
    submission?.dueDate,
    submission?.submissionType,
    submission?.submissionMethod,
    submission?.facilitySubmissions,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    signatureType,
  ]);

  // Edits land a field at a time; 500ms matches useValidatingForm in the legacy
  // form. Leading edge so arriving on the step validates immediately rather than
  // leaving the send button disabled for the first half second.
  const debouncedRevalidate = useDebouncedCallback(revalidate, 500, { leading: true });

  // `useDebouncedCallback` keeps its callback in a ref, so the wrapper's identity
  // never changes — depending on it alone would validate once on mount and never
  // pick up an edit made elsewhere (e.g. the Submission Details dialog).
  useEffect(() => {
    void debouncedRevalidate();
    return () => debouncedRevalidate.cancel();
  }, [debouncedRevalidate, revalidate]);

  // Nobody can act on issues once a submission is completed.
  const displayIssues = useMemo(
    () =>
      status === SubmissionStatus.FilingComplete || status === SubmissionStatus.NotReporting
        ? []
        : issues,
    [status, issues]
  );

  const reviewerInputs = useMemo(
    () => toReviewerInputs(buildOrderedConfig(reviewersToPersist, signer)),
    [reviewersToPersist, signer]
  );

  return { issues, revalidate, displayIssues, reviewerInputs };
}

/** Slices displayed validation issues by field, and computes reviewer/signer due-date warnings. */
export function useReviewerPreparationIssueBreakdown({
  documents,
  displayIssues,
  reviewers,
  signer,
  signatureType,
}: Pick<ReturnType<typeof useReviewerPreparationContext>, "documents"> & {
  displayIssues: Issue[];
  reviewers: Reviewer[];
  signer: SubmissionReviewerSigner | null;
  signatureType: SignatureType;
}) {
  const reviewRequiredIssues = useMemo(
    () => displayIssues.filter((i) => i.key === "reviewRequired"),
    [displayIssues]
  );
  const signatureTypeIssues = useMemo(
    () => displayIssues.filter((i) => i.key === "signatureType"),
    [displayIssues]
  );

  // What's left has no control on this step to attach to — mostly submission-level
  // fields from the Details card. Without this it only ever shows up as a disabled
  // Send for review with no stated reason.
  const unattributedIssues = useMemo(
    () =>
      displayIssues.filter(
        (i) =>
          i.modelName !== "SubmissionReviewer" &&
          !(i.modelName === "Submission" && i.key === "reviewers") &&
          i.key !== "documents" &&
          i.key !== "reviewRequired" &&
          i.key !== "signatureType"
      ),
    [displayIssues]
  );

  const signatureRequestExpiresAt = useMemo(
    () => signatureRequestExpiry(documents, DateTime.utc().startOf("day"), signatureType),
    [documents, signatureType]
  );

  const dueDateWarnings = useMemo(() => {
    const warnings: Record<string, string> = {};
    const today = DateTime.utc().startOf("day");
    for (const config of buildOrderedConfig(reviewers, signer)) {
      if (!config.dueDate?.isValid) continue;
      const due = DateTime.utc(config.dueDate.year, config.dueDate.month, config.dueDate.day);
      const warning = dueDateWarning({
        dueDate: due,
        isSigner: config.isSigner === true,
        signatureRequestExpiresAt,
        today,
      });
      if (warning) {
        warnings[config.id] = warning;
      }
    }
    return warnings;
  }, [reviewers, signer, signatureRequestExpiresAt]);

  return { reviewRequiredIssues, signatureTypeIssues, unattributedIssues, dueDateWarnings };
}
